import { type ReactNode, type ElementType } from 'react';
export interface NextgenImageProviderInterface {
  element: ElementType;
  children: ReactNode;
  useCDNForCS?: boolean;
  getImageProps?: (imgProps: ImageProps) => ImgPropsReturn;
}

export enum LoaderType {
  CONTENTSTACK = 'CONTENTSTACK',
  WEBFONTS = 'WEBFONTS',
}

export type OnLoadingComplete = (img: HTMLImageElement) => void;
export type PlaceholderValue = 'blur' | 'empty' | `data:image/${string}`;

export type ImageLoaderProps = {
  src: string;
  width: number;
  quality?: number;
};

export interface StaticImageData {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
  blurWidth?: number;
  blurHeight?: number;
}
export interface StaticRequire {
  default: StaticImageData;
}
export type StaticImport = StaticRequire | StaticImageData;

export type ImageLoader = (p: ImageLoaderProps) => string;

export type BasicImageTypes = React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

export enum ImageSourceType {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

export interface NextgenImageSrcObject {
  [ImageSourceType.MOBILE]?: string;
  [ImageSourceType.TABLET]?: string;
  [ImageSourceType.DESKTOP]?: string;
}
export interface NextgenImagePropsBase extends Omit<BasicImageTypes, 'alt' | 'src' | 'ref'> {
  src: string | NextgenImageSrcObject;
  alt: string;
  width?: number | `${number}` | undefined;
  height?: number | `${number}` | undefined;
  fill?: boolean | undefined;
  loader?: ImageLoader | undefined;
  quality?: number | `${number}` | undefined;
  priority?: boolean | undefined;
  loading?: 'eager' | 'lazy' | undefined;
  placeholder?: PlaceholderValue | undefined;
  blurDataURL?: string | undefined;
  unoptimized?: boolean | undefined;
  onLoadingComplete?: OnLoadingComplete | undefined;
  layout?: string | undefined;
  objectFit?: string | undefined;
  objectPosition?: string | undefined;
  lazyBoundary?: string | undefined;
  lazyRoot?: string | undefined;

  loaderType?: LoaderType;
  isRelativeURL?: boolean;
  fallbackQuality?: number;
  format?: string;
  forceCdnUrlConversion?: boolean;
  imageWidths?: ImageSize;
  isSensitiveField?: boolean;
}
export type ImageSize = {
  small: number;
  medium: number;
  large: number;
  xlarge: number;
};

export interface NextgenImagePropsWithRef extends NextgenImagePropsBase, React.RefAttributes<HTMLImageElement | null> {}

export type NextgenImageProps = NextgenImagePropsWithRef;

export type ForwardRefComponent<P> = React.ForwardRefExoticComponent<P & React.RefAttributes<unknown>>;

export type ImageProps = Omit<
  JSX.IntrinsicElements['img'],
  'src' | 'srcSet' | 'ref' | 'alt' | 'width' | 'height' | 'loading'
> & {
  src: string | StaticImport;
  alt: string;
  width?: number | `${number}`;
  height?: number | `${number}`;
  fill?: boolean;
  loader?: ImageLoader;
  quality?: number | `${number}`;
  priority?: boolean;
  loading?: 'eager' | 'lazy' | undefined;
  placeholder?: PlaceholderValue;
  blurDataURL?: string;
  unoptimized?: boolean;
  overrideSrc?: string;
};

export type ImgProps = Omit<ImageProps, 'src' | 'loader'> & {
  loading: 'eager' | 'lazy' | undefined;
  width: number | undefined;
  height: number | undefined;
  style: NonNullable<JSX.IntrinsicElements['img']['style']>;
  sizes: string | undefined;
  srcSet: string | undefined;
  src: string;
};

export interface ImgPropsReturn {
  props: ImgProps;
}
